import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AthleteLayout from "../common/AthleteLayout";
import { AthleteHeader } from "../common/AthleteHeader";
import { useAthleteAuthContext } from "../login/useAthleteAuthContext";
import API_URL from "../../api/API";

export const AthleteCoachedSpeedWorkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { athlete } = useAthleteAuthContext();

  const [weeks, setWeeks] = useState(location.state?.initialWeeks || []);
  const [openIndex, setOpenIndex] = useState(null);
  const [loading, setLoading] = useState(
    !Boolean(location.state?.initialWeeks),
  );

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const handleWarmupClick = () => {
    navigate(`/athlete/coached/warmup-routine-phases`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    
    // Parse as local date to avoid timezone issues
    const parts = dateString.split("-");
    if (parts.length === 3) {
      const year = parseInt(parts[0]);
      const month = parseInt(parts[1]);
      const day = parseInt(parts[2]);
      
      if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
        const date = new Date(year, month - 1, day);
        const y = date.getFullYear();
        const m = (date.getMonth() + 1).toString().padStart(2, "0");
        const d = date.getDate().toString().padStart(2, "0");
        return `${y}-${m}-${d}`;
      }
    }
    
    // Fallback
    const date = new Date(dateString);
    const y = date.getFullYear();
    const m = (date.getMonth() + 1).toString().padStart(2, "0");
    const d = date.getDate().toString().padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  useEffect(() => {
    // if parent passed weeks via state, use them and skip fetch
    if (location.state?.initialWeeks) {
      // ⭐ If child says this data is freshly updated → PERSIST it
      if (location.state.persistUpdatedWeeks) {
        setWeeks(location.state.initialWeeks);
      }

      // Still use state for first render
      setWeeks(location.state.initialWeeks);
      setLoading(false);

      if (location.state.initialWeek) {
        const index = (location.state.initialWeeks || []).findIndex(
          (w) => w._id === location.state.initialWeek._id,
        );
        if (index >= 0) setOpenIndex(index);
      }

      return;
    }

    if (!athlete?.id || !athlete?.token) {
      setLoading(false);
      return;
    }

    const fetchWorkout = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${API_URL.FRONTEND_COACHED_ATHLETE_SPEED_WORKOUT}${athlete.id}`,
          { headers: { Authorization: `Bearer ${athlete.token}` } },
        );

        const json = await response.json();

        if (!response.ok) {
          console.error("Failed to load coached speed workout:", json);
          setWeeks([]);
          return;
        }

        const rawWeeks = json.data || json.weeks || json || [];
        const normalized = (rawWeeks || []).map((w) => {
          const days = Array.isArray(w.days)
            ? w.days
            : Object.values(w.days || {});
          return { ...w, days };
        });

        const filteredWeeks = normalized.filter(
          (week) =>
            Array.isArray(week.days) &&
            week.days.some(
              (day) => Array.isArray(day.sprints) && day.sprints.length > 0,
            ),
        );

        setWeeks(filteredWeeks);
      } catch (err) {
        console.error("Error fetching speed workout:", err);
        setWeeks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkout();
  }, [athlete]);

  const handlePerformanceClick = () => {
    navigate("/athlete/coached/performance");
  };

  return (
    <AthleteLayout>
      <div className="h-full flex flex-col bg-white">
        <div className="flex-1 overflow-y-auto">
          <div>
            <AthleteHeader />
          </div>

          <div className="pb-3 mb-3 w-full max-w-2xl mx-auto flex flex-row justify-center items-center gap-10 px-5">
            <p className="w-full py-3 text-black text-xl line-clamp-2 leading-tight text-center border-b border-black/30">
              Speed Workout
            </p>
          </div>

          <div className="w-full max-w-2xl mx-auto py-3 px-5 flex flex-col">
            <div className="bg-[#f5f4f4] rounded-2xl flex justify-between gap-8 px-4 py-4 mb-4">
              <button
                onClick={handleWarmupClick}
                className="text-lightSmock text-sm xs:text-base"
              >
                Warmup 
              </button>

              <button className="text-black border-b-2 border-black text-sm xs:text-base pb-2">
                 Workout
              </button>

              <button
                onClick={handlePerformanceClick}
                className="text-lightSmock text-sm xs:text-base"
              >
                Performance
              </button>
            </div>
          </div>

          <div className="w-full max-w-2xl mx-auto text-black py-3 px-5 space-y-5">
            {loading ? (
              <p className="text-center py-10">Loading speed workout…</p>
            ) : weeks.length === 0 ? (
              <p className="text-center py-10">No speed workout found.</p>
            ) : (
              weeks.map((week, weekIndex) => (
                <div
                  key={week._id || weekIndex}
                  className={`${openIndex === weekIndex ? "!border-black/30" : ""} border border-transparent rounded-xl`}
                >
                  <button
                    className="w-full flex justify-between items-center gap-5 text-white text-base font-bold bg-black text-left px-4 py-3 rounded-lg"
                    onClick={() => toggleAccordion(weekIndex)}
                  >
                    <p className="flex items-center gap-2">
                      <span>W{weekIndex + 1}</span>
                      <span className="text-lg !leading-none opacity-70">|</span>
                      <span>
                        {formatDate(
                          week.week_start_date
                            ? week.week_start_date.substring(0, 10)
                            : week.days?.[0]?.date,
                        )}
                      </span>
                    </p>
                    <span
                      className={`transform transition-transform duration-300 flex justify-center items-center bg-darkYellow p-1 text-black leading-none w-6 h-6 text-center`}
                    >
                      {openIndex === weekIndex ? <Minus /> : <Plus />}
                    </span>
                  </button>

                  {openIndex === weekIndex && (
                    <div className="px-10 py-3">
                      {week.days
                        .filter(
                          (day) =>
                            Array.isArray(day.sprints) &&
                            day.sprints.length > 0,
                        )
                        .map((day, dayIndex) => (
                          <div
                            key={dayIndex}
                            className="w-fit flex justify-between items-center mb-0.5"
                          >
                            <button
                              onClick={() =>
                                navigate(
                                  `/athlete/coached/speed-workout/sprints`,
                                  {
                                    state: {
                                      week,
                                      day,
                                      sprints: day.sprints,
                                      parentWeeks: weeks,
                                    },
                                  },
                                )
                              }
                              className="w-full py-1 text-left text-sm font-medium text-black capitalize border-b-2 border-transparent hover:border-darkYellow"
                            >
                              <div className="text-sm">
                                <span className="font-medium">
                                  {formatDate(day.date)}
                                </span>
                                <span className="mx-2">—</span>
                                <span>{day.label || ""}</span>
                              </div>
                              <div className="text-xs text-grayish">
                                {day.training_focus || "Training Focus"}
                              </div>
                            </button>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </AthleteLayout>
  );
};

const Plus = () => {
  return (
    <svg
      width="24"
      className="w-full h-full"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M0 0H24V24H0V0Z" fill="#C19E42" />
      <path d="M11 4.5H13V19.5H11V4.5Z" fill="black" />
      <path d="M4.5 13V11H19.5V13H4.5Z" fill="black" />
    </svg>
  );
};

const Minus = () => {
  return (
    <svg
      width="24"
      className="w-full h-full"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M0 0H24V24H0V0Z" fill="#C19E42" />
      {/* <path d="M10.5 4H13.5V20H10.5V4Z" fill="black" /> */}
      <path d="M4.5 13V11H19.5V13H4.5Z" fill="black" />
    </svg>
  );
};
